import { useState, useEffect } from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Users,
  UserPlus,
  Loader2,
  Eye,
  EyeOff,
  Shield,
  Mail,
  User,
  Calendar,
  MoreHorizontal,
  Edit,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { USER_ROLES, ROLE_OPTIONS, type UserRole } from '../types/roles';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../store/useStore';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

const createUserSchema = z.object({
  email: z.string().email('Email non valida').min(1, 'Email richiesta'),
  password: z.string().min(6, 'Password deve essere almeno 6 caratteri'),
  full_name: z.string().min(2, 'Nome completo richiesto'),
  role: z.enum(USER_ROLES, {
    required_error: 'Ruolo richiesto'
  })
});

type CreateUserForm = z.infer<typeof createUserSchema>;

const roleLabels = {
  admin: 'Amministratore',
  commerciale: 'Commerciale (Vendite)',
  amministrazione: 'Amministrazione',
  lettore: 'Lettore',
  production: 'Produzione',
  customer_user: 'Utente Cliente',
  sales: 'Vendite (Esteso)',
  lab: 'Laboratorio',
  warehouse: 'Magazzino'
};

const roleDescriptions = {
  admin: 'Accesso completo a tutte le funzionalità',
  commerciale: 'Gestione vendite e clienti (tutte le funzioni tranne gestione utenti)',
  amministrazione: 'Amministrazione e gestione contabile/amministrativa',
  lettore: 'Solo visualizzazione dati',
  production: 'Gestione produzione e magazzino',
  customer_user: 'Accesso limitato per clienti',
  sales: 'Vendite avanzate (report + listini)',
  lab: 'Accesso alla sezione LAB e insight qualità',
  warehouse: 'Gestione operativa del magazzino (carichi/scarichi)'
};

const getRoleBadge = (role: string) => {
  const styles = {
    admin: 'bg-red-100 text-red-800 border-red-200',
    commerciale: 'bg-slate-100 text-slate-800 border-slate-200',
    amministrazione: 'bg-violet-100 text-violet-800 border-violet-200',
    lettore: 'bg-gray-100 text-gray-800 border-gray-200',
    production: 'bg-orange-100 text-orange-800 border-orange-200',
    customer_user: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    sales: 'bg-lime-100 text-lime-800 border-lime-200',
    lab: 'bg-pink-100 text-pink-800 border-pink-200',
    warehouse: 'bg-emerald-100 text-emerald-800 border-emerald-200'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[role as keyof typeof styles]}`}>
      {roleLabels[role as keyof typeof roleLabels] || role}
    </span>
  );
};

export const UserManagementPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<Profile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [userToDelete, setUserToDelete] = useState<Profile | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { addNotification } = useNotifications();
  const { user: currentUser, signOut } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema)
  });

  const selectedRole = watch('role');

  const editUserSchema = z.object({
    full_name: z.string().min(2, 'Nome completo richiesto'),
    role: z.enum(USER_ROLES, {
      required_error: 'Ruolo richiesto'
    })
  });

  type EditUserForm = z.infer<typeof editUserSchema>;

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: editErrors },
    reset: resetEdit,
    setValue: setEditValue,
  } = useForm<EditUserForm>({
    resolver: zodResolver(editUserSchema)
  });

  const loadUsers = useCallback(async () => {
    try {
      // Use direct database query - check if created_at column exists
      const { data, error } = await supabase
        .from('profiles')
        .select('*');

      if (error) {
        console.error('Error loading users:', error);
        // If there's a column error, show a helpful message
        if (error.message.includes('created_at') || error.message.includes('updated_at')) {
          addNotification({
            type: 'error',
            title: 'Errore di configurazione',
            message: 'Colonne timestamp mancanti nella tabella profiles. Esegui ADD_MISSING_COLUMNS.sql'
          });
        } else if (error.message.includes('infinite recursion') || error.message.includes('policy')) {
          addNotification({
            type: 'error',
            title: 'Errore di configurazione',
            message: 'Problema con le politiche di sicurezza. Contatta l\'amministratore.'
          });
        } else {
          addNotification({
            type: 'error',
            title: 'Errore',
            message: 'Impossibile caricare la lista utenti'
          });
        }
        return;
      }

      setUsers(data || []);
    } catch (error: any) {
      console.error('Error loading users:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare la lista utenti'
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const onSubmit = async (data: CreateUserForm) => {
    setIsLoading(true);

    try {
      // Ensure current user role
      if (currentUser?.id) {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: currentUser.id,
            email: currentUser.email || '',
            full_name: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'Admin',
            role: 'admin'
          });

        if (profileError) {
          console.warn('Could not ensure admin profile:', profileError);
        } else {

        }

        // Refresh the user session to update metadata
        try {
          await supabase.auth.refreshSession();

        } catch (refreshError) {
          console.warn('Could not refresh session:', refreshError);
        }
      }

      // Use simple signup method (bypasses Edge Function issues)

      // Try signup first, if it fails due to disabled signups, create profile only
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.full_name,
            role: data.role
          }
        }
      });

      // If signup is disabled, create a profile-only user
      if (signupError && signupError.message.includes('Signups not allowed')) {

        // Create a mock user ID for profile-only user
        const mockUserId = `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create profile directly
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: mockUserId,
            email: data.email,
            full_name: data.full_name,
            role: data.role
          });

        if (profileError) {
          throw profileError;
        }

        // Return mock user data
        const mockUser = {
          id: mockUserId,
          email: data.email,
          user_metadata: {
            full_name: data.full_name,
            role: data.role
          }
        };

        // Continue with success flow

        addNotification({
          type: 'success',
          title: 'Utente creato (solo profilo)',
          message: `L'utente ${data.email} è stato creato come profilo. Per l'accesso, configurare manualmente in Supabase.`
        });

        reset();
        await loadUsers();
        return;
      }

      if (signupError) {
        console.error('Signup failed:', signupError);
        addNotification({
          type: 'error',
          title: 'Errore creazione utente',
          message: signupError.message || 'Impossibile creare l\'utente'
        });
        return;
      }

      if (!signupData.user) {
        addNotification({
          type: 'error',
          title: 'Errore creazione utente',
          message: 'Utente creato ma dati non disponibili'
        });
        return;
      }


      // Try to create profile, but don't fail if it doesn't work
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: signupData.user.id,
            email: data.email,
            full_name: data.full_name,
            role: data.role
          });

        if (profileError) {
          console.warn('Profile creation failed, but user was created:', profileError);
        } else {

        }
      } catch (profileInsertError) {
        console.warn('Profile insert failed, but user exists:', profileInsertError);
      }

      addNotification({
        type: 'success',
        title: 'Utente creato',
        message: `L'utente ${data.email} è stato creato con successo`
      });

      // Reset form and reload users
      reset();
      await loadUsers();

    } catch (error: any) {
      console.error('Error creating user:', error);
      addNotification({
        type: 'error',
        title: 'Errore creazione utente',
        message: error.message || 'Si è verificato un errore durante la creazione dell\'utente'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Il database conserva anche ruoli che l'app non assegna piu'
   * ('production', 'customer_user', 'lab'). Se apro in modifica un utente
   * che ne ha uno, il menu a tendina non avrebbe nessuna voce corrispondente
   * e resterebbe vuoto: ripiego sul ruolo minimo, cosi' chi salva sceglie
   * esplicitamente un ruolo valido.
   */
  const toAssignableRole = (role: string | null): UserRole =>
    (USER_ROLES as readonly string[]).includes(role ?? '') ? (role as UserRole) : 'lettore';

  const handleEditUser = (user: Profile) => {
    setEditingUser(user);
    resetEdit({
      full_name: user.full_name || '',
      role: toAssignableRole(user.role)
    });
    setShowEditDialog(true);
  };

  const onSubmitEdit = async (data: EditUserForm) => {
    if (!editingUser || !currentUser?.id) return;

    // Prevent admin from changing their own role
    if (editingUser.id === currentUser.id && data.role !== editingUser.role) {
      addNotification({
        type: 'error',
        title: 'Operazione non consentita',
        message: 'Non puoi modificare il tuo stesso ruolo'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Use direct database query for updating user
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          role: data.role
        })
        .eq('id', editingUser.id);

      if (error) {
        console.error('Error updating user:', error);

        // Messaggi di errore più specifici
        let errorMessage = 'Impossibile aggiornare l\'utente. Verifica i permessi.';

        if (error.message?.includes('invalid input value for enum user_role') ||
          error.message?.includes('type user_role') ||
          error.code === '23502') {
          errorMessage = `Il ruolo "${data.role}" non è valido nel database. Esegui lo script ADD_MISSING_ROLES.sql per aggiungere i ruoli mancanti all'enum user_role.`;
        } else if (error.message) {
          errorMessage = error.message;
        }

        addNotification({
          type: 'error',
          title: 'Errore aggiornamento',
          message: errorMessage
        });
        return;
      }

      // Check if role was changed
      const roleChanged = data.role !== editingUser.role;

      // Inform that user needs to logout/login if role changed
      if (roleChanged) {
        addNotification({
          type: 'success',
          title: 'Utente aggiornato',
          message: `${data.full_name} è stato aggiornato. L'utente deve effettuare logout e login per vedere il nuovo ruolo.`
        });
      } else {
        addNotification({
          type: 'success',
          title: 'Utente aggiornato',
          message: `${data.full_name} è stato aggiornato con successo`
        });
      }

      setShowEditDialog(false);
      setEditingUser(null);
      resetEdit();
      await loadUsers();

    } catch (error: any) {
      console.error('Error updating user:', error);
      addNotification({
        type: 'error',
        title: 'Errore aggiornamento',
        message: error.message || 'Si è verificato un errore durante l\'aggiornamento dell\'utente'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = (user: Profile) => {
    if (user.id === currentUser?.id) {
      addNotification({
        type: 'error',
        title: 'Operazione non consentita',
        message: 'Non puoi eliminare il tuo stesso account'
      });
      return;
    }
    setUserToDelete(user);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete || !currentUser?.id) return;

    setIsLoading(true);
    try {

      // Simple method: Delete from profiles table directly
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userToDelete.id);

      if (profileError) {
        console.error('Error deleting profile:', profileError);
        addNotification({
          type: 'error',
          title: 'Errore eliminazione',
          message: profileError.message || 'Impossibile eliminare il profilo utente.'
        });
        return;
      }


      addNotification({
        type: 'success',
        title: 'Utente eliminato',
        message: `${userToDelete.full_name || userToDelete.email} è stato eliminato con successo`
      });

      setUserToDelete(null);
      await loadUsers();

    } catch (error: any) {
      console.error('Error deleting user:', error);
      addNotification({
        type: 'error',
        title: 'Errore eliminazione',
        message: error.message || 'Si è verificato un errore durante l\'eliminazione dell\'utente'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestione Utenti</h1>
          <p className="text-sm text-gray-600 mt-1">
            Crea e gestisci gli utenti del sistema
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="w-full sm:w-auto" onClick={loadUsers} disabled={loadingUsers}>
            <Users className="w-4 h-4 mr-2" />
            Aggiorna Lista
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Create User Form */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl md:text-2xl">
              <UserPlus className="w-5 h-5" />
              <span>Nuovo Utente</span>
            </CardTitle>
            <CardDescription>
              Crea un nuovo utente con ruolo specifico
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="utente@example.com"
                  autoComplete="email"
                  {...register('email')}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="full_name">Nome Completo</Label>
                <Input
                  id="full_name"
                  type="text"
                  placeholder="Mario Rossi"
                  {...register('full_name')}
                  className={errors.full_name ? 'border-red-500' : ''}
                />
                {errors.full_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.full_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    {...register('password')}
                    className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="role">Ruolo</Label>
                <Select onValueChange={(value) => setValue('role', value as any)}>
                  <SelectTrigger className={errors.role ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Seleziona un ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-gray-500">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
                {selectedRole && (
                  <p className="mt-1 text-xs text-gray-500">
                    {roleDescriptions[selectedRole]}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creazione in corso...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Crea Utente
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl md:text-2xl">
              <Users className="w-5 h-5" />
              <span>Utenti Esistenti</span>
            </CardTitle>
            <CardDescription>
              Lista di tutti gli utenti registrati nel sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Caricamento utenti...</span>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nessun utente trovato</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((userProfile) => (
                  <div
                    key={userProfile.id}
                    className="flex items-center justify-between gap-2 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                      <div className="w-10 h-10 flex-shrink-0 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-gray-900 break-words">
                            {userProfile.full_name || 'Nome non specificato'}
                          </p>
                          {getRoleBadge(userProfile.role)}
                        </div>
                        <div className="flex flex-col gap-1 text-sm text-gray-500 sm:flex-row sm:items-center sm:gap-4">
                          <div className="flex items-center gap-1 min-w-0">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{userProfile.email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>
                              {new Date(userProfile.created_at).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditUser(userProfile)}
                        title="Modifica utente"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(userProfile)}
                        title="Elimina utente"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={userProfile.id === currentUser?.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
            <DialogDescription>
              Modifica le informazioni dell'utente selezionato
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitEdit(onSubmitEdit)} className="space-y-4">
            <div>
              <Label htmlFor="edit_full_name">Nome Completo</Label>
              <Input
                id="edit_full_name"
                {...registerEdit('full_name')}
                className={editErrors.full_name ? 'border-red-500' : ''}
              />
              {editErrors.full_name && (
                <p className="mt-1 text-sm text-red-600">{editErrors.full_name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_role">Ruolo</Label>
              <Select
                value={watch('role')}
                onValueChange={(value) => setEditValue('role', value as any)}
                disabled={editingUser?.id === currentUser?.id}
              >
                <SelectTrigger className={editErrors.role ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleziona un ruolo" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-gray-500">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editErrors.role && (
                <p className="mt-1 text-sm text-red-600">{editErrors.role.message}</p>
              )}
              {editingUser?.id === currentUser?.id && (
                <p className="mt-1 text-xs text-gray-500">
                  Non puoi modificare il tuo stesso ruolo
                </p>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2 [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  'Salva Modifiche'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span>Conferma Eliminazione</span>
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l'utente "{userToDelete?.full_name || userToDelete?.email}"?
              <br />
              <span className="text-red-600 font-medium">
                Questa azione eliminerà permanentemente l'account e tutti i dati associati.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToDelete(null)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteUser}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                'Sì, elimina utente'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default UserManagementPage;